import { sourceLink, type SourceLink } from "@/lib/source-link";
import type { CompanyWebFact, CompanyWebFactCategory } from "@shaarp/common";

/** A section of the preview: "news" is filed with "press". */
export type CompanyWebFactSectionId = Exclude<CompanyWebFactCategory, "news">;

/** A fact whose source is an http(s) page, with that source ready to link. */
export type LinkedCompanyWebFact = CompanyWebFact & { source: SourceLink };

export type CompanyWebFactSection = {
  id: CompanyWebFactSectionId;
  facts: LinkedCompanyWebFact[];
};

/** Display order, as in the contract; press and news share one section. */
const SECTION_ORDER: CompanyWebFactSectionId[] = [
  "activity",
  "financials",
  "press",
  "hiring",
  "reviews",
  "legal",
  "other",
];

const DATE_PATTERN = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

function validDate(date: string | undefined): string | undefined {
  if (!date) return undefined;
  const match = DATE_PATTERN.exec(date);
  if (!match) return undefined;
  const month = match[2] ? Number(match[2]) : 1;
  const day = match[3] ? Number(match[3]) : 1;
  return month >= 1 && month <= 12 && day >= 1 && day <= 31 ? date : undefined;
}

/**
 * The facts a preview shows, grouped by section in display order, each
 * section newest first (undated last). A fact is shown only with a source the
 * reader can open: a URL that is not http(s) drops it, and an unreadable date
 * is ignored rather than displayed.
 */
export function groupFactsBySection(
  facts: CompanyWebFact[],
): CompanyWebFactSection[] {
  const bySection = new Map<CompanyWebFactSectionId, LinkedCompanyWebFact[]>();
  for (const fact of facts) {
    const source = sourceLink(fact.url);
    if (!source) continue;
    const id: CompanyWebFactSectionId =
      fact.category === "news" ? "press" : fact.category;
    const rows = bySection.get(id) ?? [];
    rows.push({ ...fact, date: validDate(fact.date), source });
    bySection.set(id, rows);
  }

  return SECTION_ORDER.flatMap((id) => {
    const rows = bySection.get(id);
    if (!rows?.length) return [];
    return [{ id, facts: [...rows].sort(newestFirst) }];
  });
}

/** Newest first, undated last; ISO dates of any precision compare as strings. */
function newestFirst(
  a: LinkedCompanyWebFact,
  b: LinkedCompanyWebFact,
): number {
  if (a.date && b.date) return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
  if (a.date) return -1;
  if (b.date) return 1;
  return 0;
}

/** The React key of a fact row: its page and its text. */
export function factKey(fact: LinkedCompanyWebFact): string {
  return `${fact.source.href}|${fact.text}`;
}

/**
 * A fact date for display in the app language: "12 mars 2026", "mars 2026" or
 * "2026" depending on how precise the source was. Null when unreadable.
 */
export function formatFactDate(
  date: string | undefined,
  language: "en" | "fr",
): string | null {
  const valid = validDate(date);
  const match = valid ? DATE_PATTERN.exec(valid) : null;
  if (!match) return null;
  const [, year, month, day] = match;
  if (!month) return year;
  return new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "short",
    ...(day ? { day: "numeric" } : {}),
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day ?? 1))));
}
